import React, { useEffect, useMemo, useState, useContext } from "react";
import MainContext from "../../../Context/MainContext/MainContext";

import MUISkeleton from "@mui/material/Skeleton";
import MUITooltip from "@mui/material/Tooltip";
import MUITypography from "@mui/material/Typography";

import Widget from "../../Widget";
import Format from "../../../Utility/Format";
import Setting from "../../../Utility/Setting";
import User from "../../../Utility/Crud/User";
import api from "../../../Seating/Security/api";

/**
 * Dashboard widget: inbound files for the current user's inbound file recipients.
 *
 * @param {object} props
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardInboundFiles(props) {
  const mainContext = useContext(MainContext);
  const [inboundFileRecords, setInboundFileRecords] = useState([]);
  const [isLoadingInboundFileCount, setIsLoadingInboundFileCount] =
    useState(true);
  const [isErrorInboundFileCount, setIsErrorInboundFileCount] = useState(false);
  const [recipientSettingRefreshCounter, setRecipientSettingRefreshCounter] =
    useState(0);

  const recipientOptionUsers = useMemo(() => {
    const currentUser = mainContext.currentUser;
    const filteredUsers = User.getFilteredUsers(
      currentUser,
      mainContext.userprofileLocationsIndexed,
      mainContext.locationsIndexed,
      mainContext.usersIndexed,
      mainContext.salesPcrLinksIndexed,
      true,
    );
    const recipientOptionUserById = new Map(
      filteredUsers.map((user) => [user.id, user]),
    );

    recipientOptionUserById.set(currentUser.id, currentUser);

    return User.sort(
      [...recipientOptionUserById.values()].filter((user) => {
        return Format.phone(user.fax);
      }),
    );
  }, [
    mainContext.currentUser,
    mainContext.locationsIndexed,
    mainContext.salesPcrLinksIndexed,
    mainContext.userprofileLocationsIndexed,
    mainContext.usersIndexed,
  ]);

  const recipientUserProfileIds = useMemo(() => {
    return recipientOptionUsers.map((user) => user.id).filter(Boolean);
  }, [recipientOptionUsers]);

  const selectedRecipientContacts = useMemo(() => {
    const recipientContactByUserProfileId = new Map(
      recipientOptionUsers.map((user) => [user.id, Format.phone(user.fax)]),
    );
    const validRecipientContactSet = new Set(
      recipientContactByUserProfileId.values(),
    );
    const savedRecipientContacts = Setting.get("inboundFiles.recipient", false);
    const validSavedRecipientContacts = Array.isArray(savedRecipientContacts)
      ? savedRecipientContacts.filter((recipientContact) => {
          return validRecipientContactSet.has(recipientContact);
        })
      : [];

    if (validSavedRecipientContacts.length > 0) {
      return validSavedRecipientContacts;
    }

    const currentUserRecipientContact = recipientContactByUserProfileId.get(
      mainContext.currentUser.id,
    );
    return currentUserRecipientContact ? [currentUserRecipientContact] : [];
  }, [
    mainContext.currentUser.id,
    recipientOptionUsers,
    recipientSettingRefreshCounter,
  ]);

  const inboundFileCount = useMemo(() => {
    const selectedRecipientContactSet = new Set(selectedRecipientContacts);

    return inboundFileRecords.filter((inboundFileRecord) => {
      return (
        !inboundFileRecord.deleted &&
        selectedRecipientContactSet.has(
          Format.phone(inboundFileRecord.recipientContact),
        )
      );
    }).length;
  }, [inboundFileRecords, selectedRecipientContacts]);

  const selectedRecipientSummaries = useMemo(() => {
    const selectedRecipientContactSet = new Set(selectedRecipientContacts);
    const inboxFaxCountByRecipientContact = inboundFileRecords.reduce(
      (faxCountByRecipientContact, inboundFileRecord) => {
        if (inboundFileRecord.deleted) {
          return faxCountByRecipientContact;
        }

        const recipientContact = Format.phone(
          inboundFileRecord.recipientContact,
        );
        faxCountByRecipientContact[recipientContact] =
          (faxCountByRecipientContact[recipientContact] || 0) + 1;
        return faxCountByRecipientContact;
      },
      {},
    );

    return recipientOptionUsers
      .filter((user) => {
        return selectedRecipientContactSet.has(Format.phone(user.fax));
      })
      .map((user) => ({
        count: inboxFaxCountByRecipientContact[Format.phone(user.fax)] || 0,
        user: user,
      }))
      .sort((firstSummary, secondSummary) => {
        return (
          secondSummary.count - firstSummary.count ||
          User.getDisplayName(firstSummary.user).localeCompare(
            User.getDisplayName(secondSummary.user),
          )
        );
      });
  }, [inboundFileRecords, recipientOptionUsers, selectedRecipientContacts]);

  const selectedRecipientSummaryText = selectedRecipientSummaries
    .map((selectedRecipientSummary) => {
      return `${User.getAbbreviatedDisplayName(selectedRecipientSummary.user)} (${Format.number(selectedRecipientSummary.count)})`;
    })
    .join(", ");

  useEffect(() => {
    const handleRecipientSettingChange = () => {
      setRecipientSettingRefreshCounter(
        (previousCounter) => previousCounter + 1,
      );
    };
    const handleStorageChange = (storageEvent) => {
      if (storageEvent.key === "inboundFiles.recipient") {
        handleRecipientSettingChange();
      }
    };

    window.addEventListener(
      "setting.inboundFiles.recipient",
      handleRecipientSettingChange,
    );
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        "setting.inboundFiles.recipient",
        handleRecipientSettingChange,
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (recipientUserProfileIds.length === 0) {
      setInboundFileRecords([]);
      setIsLoadingInboundFileCount(false);
      setIsErrorInboundFileCount(false);
      return;
    }

    let isCancelled = false;

    setIsLoadingInboundFileCount(true);
    setIsErrorInboundFileCount(false);

    api
      .send("POST", "/inboundFile/getActiveInboundFiles", {
        userprofileids: recipientUserProfileIds,
      })
      .then((inboundFileRecords) => {
        if (isCancelled) {
          return;
        }

        if (!Array.isArray(inboundFileRecords)) {
          throw new Error("The inbound file response must be an array.");
        }

        setInboundFileRecords(inboundFileRecords);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setInboundFileRecords([]);
        setIsErrorInboundFileCount(true);
      })
      .finally(() => {
        if (isCancelled) {
          return;
        }

        setIsLoadingInboundFileCount(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [recipientUserProfileIds]);

  return (
    <Widget
      getTitle={() => props.dashboardWidget.name}
      getSubtitle={() =>
        !isLoadingInboundFileCount && selectedRecipientSummaryText ? (
          <MUITooltip
            title={selectedRecipientSummaries.map(
              (selectedRecipientSummary) => (
                <div key={selectedRecipientSummary.user.id}>
                  {User.getDisplayName(selectedRecipientSummary.user)} (
                  {Format.number(selectedRecipientSummary.count)})
                </div>
              ),
            )}
            placement="bottom"
          >
            <span
              style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedRecipientSummaryText}
            </span>
          </MUITooltip>
        ) : null
      }
      isActive={() => props.isActive}
      onClick={() => props.onActiveWidgetChange()}
      renderContent={() =>
        isLoadingInboundFileCount ? (
          <MUISkeleton height={32} variant="rounded" width="100%" />
        ) : (
          <MUITypography
            component="div"
            variant="h5"
            color={isErrorInboundFileCount ? "error" : "text.primary"}
          >
            {isErrorInboundFileCount ? "—" : Format.number(inboundFileCount)}
          </MUITypography>
        )
      }
    />
  );
}
